import {
  PaymentGatewayAdapter,
  PaymentData,
  PaymentToken,
  GatewayConfig,
  GatewayInitializationError
} from '../../types/payment';

/**
 * Base abstract class for payment gateway adapters
 * Provides common functionality and enforces interface compliance
 */
export abstract class BasePaymentGatewayAdapter implements PaymentGatewayAdapter {
  protected config?: GatewayConfig;
  protected initialized: boolean = false;

  constructor(public readonly provider: string) {}

  /**
   * Check if this gateway adapter is available/supported
   */
  isAvailable(): boolean {
    return true; // Override in subclasses if needed
  }

  /**
   * Initialize the gateway - must be implemented by subclasses
   */
  abstract initialize(config: GatewayConfig): Promise<void>;

  /**
   * Tokenize payment data - must be implemented by subclasses
   */
  abstract tokenizePayment(paymentData: PaymentData): Promise<PaymentToken>;

  /**
   * Get payment method info for display
   */
  getPaymentMethodInfo(token: PaymentToken): {
    brand: string;
    last4: string;
    displayName: string;
  } {
    const brandMap: Record<string, string> = {
      'visa': 'Visa',
      'mastercard': 'Mastercard',
      'amex': 'American Express',
      'discover': 'Discover',
      'diners': 'Diners Club',
      'jcb': 'JCB',
      'unknown': 'Card'
    };

    const brand = brandMap[token.card_brand.toLowerCase()] || token.card_brand;

    return {
      brand,
      last4: token.last_four_digits,
      displayName: `${brand} •••• ${token.last_four_digits}`
    };
  }

  /**
   * Cleanup resources - override in subclasses if needed
   */
  cleanup(): void {
    this.initialized = false;
    this.config = undefined;
  }

  /**
   * Validate that gateway is initialized before use
   */
  protected requireInitialized(): void {
    if (!this.initialized || !this.config) {
      throw new GatewayInitializationError(
        `${this.provider} gateway must be initialized before use`,
        'not_initialized',
        this.provider
      );
    }
  }

  /**
   * Load external script dynamically
   */
  protected loadScript(src: string, id?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      if (id && document.getElementById(id)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      if (id) script.id = id;
      script.async = true;

      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

      document.head.appendChild(script);
    });
  }

  /**
   * Validate card number format
   */
  protected validateCardNumber(cardNumber: string): boolean {
    const cleaned = cardNumber.replace(/\s/g, '');
    return /^[0-9]{13,19}$/.test(cleaned);
  }

  /**
   * Validate expiry date
   */
  protected validateExpiry(month: string, year: string): boolean {
    const monthNum = parseInt(month);
    const yearNum = parseInt(year);

    if (monthNum < 1 || monthNum > 12) return false;

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    if (yearNum < currentYear) return false;
    if (yearNum === currentYear && monthNum < currentMonth) return false;

    return true;
  }

  /**
   * Validate CVV format
   */
  protected validateCvv(cvv: string): boolean {
    return /^[0-9]{3,4}$/.test(cvv);
  }

  /**
   * Validate complete payment data
   */
  protected validatePaymentData(paymentData: PaymentData): void {
    const errors: string[] = [];

    if (!this.validateCardNumber(paymentData.cardNumber)) {
      errors.push('Invalid card number');
    }

    if (!this.validateExpiry(paymentData.expiryMonth, paymentData.expiryYear)) {
      errors.push('Invalid expiry date');
    }

    if (!this.validateCvv(paymentData.cvv)) {
      errors.push('Invalid CVV');
    }

    if (!paymentData.cardholderName.trim()) {
      errors.push('Cardholder name is required');
    }

    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`);
    }
  }

  /**
   * Detect card brand from card number
   */
  protected detectCardBrand(cardNumber: string): string {
    const cleaned = cardNumber.replace(/\s/g, '');

    if (/^4/.test(cleaned)) return 'visa';
    if (/^5[1-5]/.test(cleaned)) return 'mastercard';
    if (/^3[47]/.test(cleaned)) return 'amex';
    if (/^6(?:011|5)/.test(cleaned)) return 'discover';
    if (/^3[0689]/.test(cleaned)) return 'diners';
    if (/^35/.test(cleaned)) return 'jcb';

    return 'unknown';
  }
}
